// src/pages/SelectWarehouse/SelectWarehouse.js
import { useContext, useEffect, useMemo, useRef } from 'react'
import { Redirect, useHistory } from 'react-router-dom'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { UserContext } from '../../hooks/UserContext'
import { fetchWarehousesOpen } from '../../services/warehouseSevices'
import { getWarehouseRedirectPath } from '../../utils/warehouseRedirect'
import { isSuperAdminUser } from '../../utils/userUtils'
import { Loader } from '../../components/Loader'
import cubboLogo from '../../assets/logo-2.png'

export const SelectWarehouse = () => {
  const history = useHistory()
  const { user, setUser } = useContext(UserContext)
  const { t, i18n } = useTranslation()

  const isSuperAdmin = isSuperAdminUser(user)

  const { data, isLoading, isError, error } = useQuery('warehouses_open', fetchWarehousesOpen, { refetchOnWindowFocus: false })

  const accessibleWarehouses = useMemo(() => {
    if (!data) return []
    const warehouses = Array.isArray(data) ? data : data.warehouses || []
    if (isSuperAdmin || user?.read_all_warehouses) return warehouses
    if (!user?.warehouse_ids) return []
    return warehouses.filter(w => user.warehouse_ids.includes(w.id))
  }, [data, user, isSuperAdmin])

  const autoRedirected = useRef(false)

  useEffect(() => {
    if (accessibleWarehouses.length === 1 && !autoRedirected.current) {
      autoRedirected.current = true
      const warehouse = accessibleWarehouses[0]
      setUser({ ...user, current_warehouse: warehouse })
      changeLanguage(warehouse.country)
      const path = getWarehouseRedirectPath(user, warehouse.id)
      history.replace(path)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessibleWarehouses])

  const warehousesByCountry = useMemo(() => {
    const grouped = {}
    accessibleWarehouses.forEach(warehouse => {
      const country = warehouse.country || 'Otro'
      if (!grouped[country]) grouped[country] = []
      grouped[country].push(warehouse)
    })
    // Sort countries alphabetically, sort warehouses within each country
    const sortedKeys = Object.keys(grouped).sort()
    const sorted = {}
    sortedKeys.forEach(key => {
      sorted[key] = grouped[key].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    })
    return sorted
  }, [accessibleWarehouses])

  const changeLanguage = country => {
    if (country === 'BR') {
      i18n.changeLanguage('pt')
    } else {
      i18n.changeLanguage('es')
    }
  }

  const onSelectWarehouse = warehouse => {
    setUser({ ...user, current_warehouse: warehouse })
    changeLanguage(warehouse.country)
    const path = getWarehouseRedirectPath(user, warehouse.id)
    history.push(path)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary-orange-50 flex items-center justify-center">
        <Loader show={true} />
      </div>
    )
  }

  if (isError) {
    // 401 ONLY. An expired or unresolvable session is not a data error; it used
    // to render as "error loading warehouses", sending people to report a broken
    // warehouse list when their session had simply ended. Declarative
    // <Redirect>, never history.replace during render.
    //
    // 403 must NOT redirect. WarehousePolicy#index? is
    // `is_granted('WAREHOUSES_READ_ALL') || user.warehouses.exists?`, so an
    // account with no warehouse assignments is authenticated and merely
    // unauthorised. Sending it to /login would authenticate fine, come back,
    // 403 again, and loop forever. It gets the no-warehouses message instead,
    // which is both the true diagnosis and actionable.
    const status = error?.response?.status
    if (status === 401) {
      return <Redirect to="/login" />
    }
    if (status === 403) {
      return (
        <div className="min-h-screen bg-secondary-orange-50 flex items-center justify-center px-6">
          <p className="text-white text-center max-w-md">{t('select_warehouse.no_warehouses')}</p>
        </div>
      )
    }

    return (
      <div className="min-h-screen bg-secondary-orange-50 flex items-center justify-center">
        <p className="text-red-500">{t('select_warehouse.error_loading')}</p>
      </div>
    )
  }

  // Loaded fine and there is genuinely nothing to show. Distinct from the error
  // above: this is an account with no warehouse assignments, which no amount of
  // retrying fixes. Naming it is the difference between an operator waiting and
  // an operator asking an admin.
  if (accessibleWarehouses.length === 0) {
    return (
      <div className="min-h-screen bg-secondary-orange-50 flex items-center justify-center px-6">
        <p className="text-white text-center max-w-md">{t('select_warehouse.no_warehouses')}</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-secondary-orange-50 flex flex-col items-center py-16 px-4">
      <img src={cubboLogo} alt="Cubbo WMS" className="h-10 mb-4" />
      <p className="text-sm text-white mb-10">{t('select_warehouse.subtitle')}</p>

      <div className="w-full max-w-md">
        {Object.entries(warehousesByCountry).map(([country, warehouses]) => (
          <div key={country} className="mb-6">
            <p className="text-xs font-semibold text-white uppercase tracking-wide mb-2 pl-1">{country}</p>
            <div className="flex flex-col gap-2">
              {warehouses.map(warehouse => (
                <button
                  key={warehouse.id}
                  onClick={() => onSelectWarehouse(warehouse)}
                  className="flex items-center justify-between bg-white bg-opacity-10 border border-white border-opacity-20 rounded-lg px-5 py-3.5 text-left hover:bg-opacity-20 transition-all"
                >
                  <div>
                    <div className="font-semibold text-sm text-white">{warehouse.name}</div>
                    <div className="text-xs text-white text-opacity-70">{warehouse.state || warehouse.city || ''}</div>
                  </div>
                  <span className="text-white text-opacity-50 text-lg">›</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
